import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { GoogleMap } from "@react-google-maps/api";
import { useNavigate } from "react-router-dom";
import DashboardMainContainer from "../common/Containers/DashboardMainContainer";
import {
  AdminPanelTitle,
  AdminMetric,
  AdminMetadataText,
  AdminSupportingText,
  AdminAlertText,
  AdminRowAction,
  AdminFilterChipButton,
} from '../../sections/AdminDashboard/shared/adminUx';
import { AdminWidgetHeader, AdminWidgetPeriodFilter } from './AdminWidgetChrome';
import { useGoogleMaps } from '../../context/GoogleMapsProvider';
import useAuth from '../../Hooks/useAuth';

const PostCoverageMapWidget = () => {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const [coverageFilterPeriod, setCoverageFilterPeriod] = useState('7 Days');
  const [selectedPost, setSelectedPost] = useState(null);
  const [hoveredPin, setHoveredPin] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const mapRef = useRef(null);
  const profileImageRef = useRef(null);

  // Memoize user profile data to prevent recalculation
  const userProfile = useMemo(() => ({
    profilePicture: auth?.user?.profilePicture,
    initial: auth?.user?.firstName?.[0] || auth?.user?.name?.[0] || 'U'
  }), [auth?.user?.profilePicture, auth?.user?.firstName, auth?.user?.name]);

  // Dynamic post location data for different periods
  const postLocationsByPeriod = {
    '7 Days': [
      {
        id: 1,
        x: 25, y: 30,
        title: 'Expert General Plumbing Solutions for Every Home',
        views: { count: '4.1 K', label: 'Views' },
        saves: { count: '567', label: 'Saves' },
        shares: { count: '123', label: 'Shares' },
        lastActivity: '2 hours ago'
      },
      {
        id: 2,
        x: 45, y: 25,
        title: 'Expert General Plumbing Solutions for Every Home',
        views: { count: '3.2 K', label: 'Views' },
        saves: { count: '456', label: 'Saves' },
        shares: { count: '89', label: 'Shares' },
        lastActivity: '1 day ago'
      },
      {
        id: 3,
        x: 60, y: 35,
        title: 'Expert General Plumbing Solutions for Every Home',
        views: { count: '2.8 K', label: 'Views' },
        saves: { count: '389', label: 'Saves' },
        shares: { count: '67', label: 'Shares' },
        lastActivity: '2 days ago'
      },
      {
        id: 4,
        x: 35, y: 45,
        title: 'Expert General Plumbing Solutions for Every Home',
        views: { count: '2.1 K', label: 'Views' },
        saves: { count: '234', label: 'Saves' },
        shares: { count: '45', label: 'Shares' },
        lastActivity: '3 days ago'
      },
      {
        id: 5,
        x: 50, y: 55,
        title: 'Expert General Plumbing Solutions for Every Home',
        views: { count: '1.8 K', label: 'Views' },
        saves: { count: '178', label: 'Saves' },
        shares: { count: '32', label: 'Shares' },
        lastActivity: '4 days ago'
      }
    ],
    '14 Days': [
      {
        id: 6,
        x: 20, y: 70,
        title: 'Expert General Plumbing Solutions for Every Home',
        views: { count: '5.2 K', label: 'Views' },
        saves: { count: '789', label: 'Saves' },
        shares: { count: '156', label: 'Shares' },
        lastActivity: '1 hour ago'
      },
      {
        id: 7,
        x: 75, y: 20,
        title: 'Expert General Plumbing Solutions for Every Home',
        views: { count: '6.8 K', label: 'Views' },
        saves: { count: '923', label: 'Saves' },
        shares: { count: '234', label: 'Shares' },
        lastActivity: '30 minutes ago'
      },
      {
        id: 8,
        x: 40, y: 20,
        title: 'Expert General Plumbing Solutions for Every Home',
        views: { count: '3.5 K', label: 'Views' },
        saves: { count: '445', label: 'Saves' },
        shares: { count: '78', label: 'Shares' },
        lastActivity: '6 hours ago'
      },
      {
        id: 9,
        x: 65, y: 50,
        title: 'Expert General Plumbing Solutions for Every Home',
        views: { count: '2.9 K', label: 'Views' },
        saves: { count: '312', label: 'Saves' },
        shares: { count: '56', label: 'Shares' },
        lastActivity: '8 days ago'
      },
      {
        id: 10,
        x: 30, y: 15,
        title: 'Expert General Plumbing Solutions for Every Home',
        views: { count: '4.7 K', label: 'Views' },
        saves: { count: '623', label: 'Saves' },
        shares: { count: '134', label: 'Shares' },
        lastActivity: '10 days ago'
      }
    ],
    '30 Days': [
      {
        id: 11,
        x: 70, y: 60,
        title: 'Expert General Plumbing Solutions for Every Home',
        views: { count: '8.1 K', label: 'Views' },
        saves: { count: '1.2K', label: 'Saves' },
        shares: { count: '267', label: 'Shares' },
        lastActivity: '5 hours ago'
      },
      {
        id: 12,
        x: 25, y: 10,
        title: 'Expert General Plumbing Solutions for Every Home',
        views: { count: '7.8 K', label: 'Views' },
        saves: { count: '1.1K', label: 'Saves' },
        shares: { count: '289', label: 'Shares' },
        lastActivity: '15 days ago'
      },
      {
        id: 13,
        x: 55, y: 40,
        title: 'Expert General Plumbing Solutions for Every Home',
        views: { count: '6.9 K', label: 'Views' },
        saves: { count: '945', label: 'Saves' },
        shares: { count: '198', label: 'Shares' },
        lastActivity: '20 days ago'
      },
      {
        id: 14,
        x: 38, y: 65,
        title: 'Expert General Plumbing Solutions for Every Home',
        views: { count: '5.3 K', label: 'Views' },
        saves: { count: '723', label: 'Saves' },
        shares: { count: '145', label: 'Shares' },
        lastActivity: '25 days ago'
      },
      {
        id: 15,
        x: 68, y: 28,
        title: 'Expert General Plumbing Solutions for Every Home',
        views: { count: '4.8 K', label: 'Views' },
        saves: { count: '612', label: 'Saves' },
        shares: { count: '123', label: 'Shares' },
        lastActivity: '28 days ago'
      }
    ],
    '90 Days': [
      {
        id: 16,
        x: 32, y: 52,
        title: 'Expert General Plumbing Solutions for Every Home',
        views: { count: '15.2 K', label: 'Views' },
        saves: { count: '2.3K', label: 'Saves' },
        shares: { count: '512', label: 'Shares' },
        lastActivity: '2 hours ago'
      },
      {
        id: 17,
        x: 62, y: 18,
        title: 'Expert General Plumbing Solutions for Every Home',
        views: { count: '12.8 K', label: 'Views' },
        saves: { count: '1.9K', label: 'Saves' },
        shares: { count: '423', label: 'Shares' },
        lastActivity: '45 days ago'
      },
      {
        id: 18,
        x: 48, y: 72,
        title: 'Expert General Plumbing Solutions for Every Home',
        views: { count: '11.5 K', label: 'Views' },
        saves: { count: '1.7K', label: 'Saves' },
        shares: { count: '378', label: 'Shares' },
        lastActivity: '60 days ago'
      },
      {
        id: 19,
        x: 28, y: 38,
        title: 'Expert General Plumbing Solutions for Every Home',
        views: { count: '9.7 K', label: 'Views' },
        saves: { count: '1.4K', label: 'Saves' },
        shares: { count: '289', label: 'Shares' },
        lastActivity: '75 days ago'
      },
      {
        id: 20,
        x: 72, y: 45,
        title: 'Expert General Plumbing Solutions for Every Home',
        views: { count: '8.9 K', label: 'Views' },
        saves: { count: '1.3K', label: 'Saves' },
        shares: { count: '256', label: 'Shares' },
        lastActivity: '85 days ago'
      }
    ]
  };

  // Get current period data
  const postLocations = postLocationsByPeriod[coverageFilterPeriod];

  // Google Maps API configuration
  const { isLoaded, loadError } = useGoogleMaps();

  const onLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  // Map center (North Melbourne) - memoized
  const center = useMemo(() => ({ lat: -37.8063, lng: 144.9518 }), []);

  // Map container style - memoized
  const mapContainerStyle = useMemo(() => ({ width: "100%", height: "100%" }), []);

  const onUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  // Map control functions
  const focusOnMySuburb = useCallback(() => {
    console.log('Focus on My Suburb clicked'); // Debug log
    if (mapRef.current) {
      console.log('Map ref exists, focusing...'); // Debug log
      // Use a slight delay to ensure map is ready
      setTimeout(() => {
        if (mapRef.current) {
          // Focus on North Melbourne (or you can change this to any suburb coordinates)
          mapRef.current.panTo({ lat: -37.8063, lng: 144.9518 });
          mapRef.current.setZoom(12); // More zoomed in - street level view
          console.log('Map focused and zoomed'); // Debug log
        }
      }, 100);
    } else {
      console.log('Map ref is null'); // Debug log
    }
  }, []);

  const resetView = useCallback(() => {
    if (mapRef.current) {
      mapRef.current.panTo(center);
      mapRef.current.setZoom(9);
    }
  }, [center]);

  const togglePosts = useCallback(() => {
    // Toggle post markers visibility (you can implement this logic)
    console.log('Toggle posts visibility');
  }, []);

  const animatePins = useCallback(() => {
    setIsAnimating(true);
    // Reset animation after duration
    setTimeout(() => {
      setIsAnimating(false);
    }, 500); // Shorter animation duration
  }, []);

  // Custom map styles - memoized
  const mapStyles = useMemo(() => ([
    {
      featureType: 'administrative',
      elementType: 'geometry',
      stylers: [{ visibility: 'simplified' }]
    },
    {
      featureType: 'landscape',
      stylers: [{ color: '#f8f9fa' }]
    },
    {
      featureType: 'water',
      stylers: [{ color: '#e3f2fd' }]
    }
  ]), []);

  // Options - memoized
  const mapOptions = useMemo(() => ({
    styles: mapStyles,
    mapTypeId: 'roadmap',
    disableDefaultUI: true
  }), [mapStyles]);

  // Set first pin as hovered by default
  useEffect(() => {
    if (postLocations.length > 0) {
      setHoveredPin(postLocations[3]);
    }
  }, []);

  // Preload user profile image to prevent reloading on each hover
  useEffect(() => {
    if (userProfile.profilePicture) {
      const img = new Image();
      img.src = userProfile.profilePicture;
      profileImageRef.current = img;
    }
  }, [userProfile.profilePicture]);

  return (
    <>
      <style>{`
        @keyframes pinDrop {
          0% {
            transform: translateY(-30px) scale(0.9);
            opacity: 0.7;
          }
          15% {
            opacity: 1;
            transform: translateY(-5px) scale(1.05);
          }
          30% {
            transform: translateY(8px) scale(0.98);
          }
          45% {
            transform: translateY(-2px) scale(1.01);
          }
          60% {
            transform: translateY(1px) scale(0.99);
          }
          100% {
            transform: translateY(0) scale(1);
            opacity: 1;
          }
        }
        
        .pin-drop-animation {
          animation: pinDrop 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
        }
      `}</style>
      <DashboardMainContainer className="masonry-item w-[550px] h-[550px] flex flex-col overflow-hidden">
        <AdminWidgetHeader title="Post Coverage Map">
          <AdminWidgetPeriodFilter
            period={coverageFilterPeriod}
            onPeriodChange={setCoverageFilterPeriod}
          />
        </AdminWidgetHeader>

        <div className="flex-1 flex">
        {/* Map - full width */}
        <div className={`flex-1 relative min-w-0 ${process.env.NODE_ENV !== 'production' ? 'gm-dev-mask' : ''}`}>
          {process.env.NODE_ENV !== 'production' && (
            <style>{`
              .gm-dev-mask > div:nth-child(2) > div:nth-child(2) { display: none !important; }
            `}</style>
          )}
          {isLoaded ? (
                         <GoogleMap
               mapContainerStyle={mapContainerStyle}
               center={center}
               zoom={9}
               onLoad={onLoad}
               onUnmount={onUnmount}
                                                               options={mapOptions}
             >
                             {/* Custom Post Pins */}
               {postLocations.map((post) => (
                 <div
                   key={post.id}
                   className={`absolute cursor-pointer group ${
                     isAnimating ? 'pin-drop-animation' : ''
                   }`}
                   style={{ 
                     left: `${post.x}%`, 
                     top: isAnimating ? '-40px' : `${post.y}%`,
                     transition: isAnimating ? 'none' : 'all 0.3s ease-out',
                     animationDelay: '0ms',
                     animationDuration: '0.8s',
                     animationFillMode: 'forwards',
                     animationTimingFunction: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)'
                   }}
                   onMouseEnter={() => setHoveredPin(post)}
                   onMouseLeave={() => setHoveredPin(null)}
                   onClick={() => setSelectedPost(post)}
                 >
                   {/* Pin Icon */}
                   <div className="relative">
                     <svg className="w-7 h-7 drop-shadow-lg" viewBox="0 0 24 24">
                       <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#2E00FF"/>
                       <circle cx="12" cy="9" r="2.5" fill="white"/>
                     </svg>
                     
                    {/* Hover Popup */}
                    {hoveredPin && hoveredPin.id === post.id && (
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 bg-white rounded-xl shadow-xl p-4 min-w-[220px] max-w-[220px] z-20">
                        <div className="flex items-center gap-2 mb-2">
                          {/* User Profile Picture */}
                          <div className="flex-shrink-0">
                            {userProfile.profilePicture ? (
                              <img 
                                src={userProfile.profilePicture} 
                                alt="User profile" 
                                className="w-7 h-7 rounded-full object-cover border border-lightgray"
                                loading="eager"
                                decoding="async"
                                style={{ imageRendering: 'auto' }}
                              />
                            ) : (
                              <div className="w-7 h-7 rounded-full bg-primaryblue flex items-center justify-center text-white text-xs font-semibold">
                                {userProfile.initial}
                              </div>
                            )}
                          </div>
                          {/* Title */}
                          <AdminSupportingText
                            className="text-offblack truncate flex-1 font-medium"
                            title={post.title}
                          >
                            {post.title}
                          </AdminSupportingText>
                        </div>
                        <div className="flex justify-between items-center mb-2">
                          <div className="text-center">
                            <AdminMetric size="small" className="text-offblack tabular-nums">
                              {post.views.count}
                            </AdminMetric>
                            <AdminPanelTitle className="text-offblack">{post.views.label}</AdminPanelTitle>
                          </div>
                          <div className="text-center border-l border-r border-mediumgray px-5">
                            <AdminMetric size="small" className="text-offblack tabular-nums">
                              {post.saves.count}
                            </AdminMetric>
                            <AdminPanelTitle className="text-offblack">{post.saves.label}</AdminPanelTitle>
                          </div>
                          <div className="text-center">
                            <AdminMetric size="small" className="text-offblack tabular-nums">
                              {post.shares.count}
                            </AdminMetric>
                            <AdminPanelTitle className="text-offblack">{post.shares.label}</AdminPanelTitle>
                          </div>
                        </div>
                        <AdminMetadataText className="text-offblack mb-3 block">
                          Last activity {post.lastActivity}
                        </AdminMetadataText>
                        <AdminRowAction
                          type="button"
                          className="!w-full"
                          onClick={() => navigate(`/business-dashboard/posts/edit/${post.id}`)}
                        >
                          Edit post
                        </AdminRowAction>
                         
                         {/* Arrow pointing down */}
                         <div className="absolute top-full left-1/2 transform -translate-x-1/2">
                           <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-white"></div>
                         </div>
                       </div>
                     )}
                   </div>
                 </div>
               ))}

            </GoogleMap>
          ) : loadError ? (
            <div className="w-full h-full min-h-[300px] bg-mistygray border border-lightgray flex items-center justify-center">
              <div className="text-center p-4">
                <AdminAlertText className="text-alertred mb-2 block">Google Maps Error</AdminAlertText>
                <AdminSupportingText className="text-offblack block">
                  Failed to load Google Maps API
                </AdminSupportingText>
                <AdminMetadataText className="text-mediumgray mt-2 block">
                  Check your API key configuration
                </AdminMetadataText>
              </div>
            </div>
          ) : (
            <div className="w-full h-full min-h-[300px] bg-mistygray border border-lightgray flex items-center justify-center">
              <div className="text-center">
                <AdminSupportingText className="text-offblack">Loading Google Maps...</AdminSupportingText>
              </div>
            </div>
          )}

          {/* Map Controls */}
          <div className="absolute bottom-4 px-4 w-full flex flex-row justify-between gap-2">
            <AdminFilterChipButton
              type="button"
              className="!bg-white !text-offblack shadow-md hover:!bg-lightgray"
              onClick={focusOnMySuburb}
            >
              Focus on my suburb
            </AdminFilterChipButton>
            <AdminFilterChipButton
              type="button"
              className="!bg-white !text-offblack shadow-md hover:!bg-lightgray"
              onClick={resetView}
            >
              Reset view
            </AdminFilterChipButton>
            <AdminFilterChipButton
              type="button"
              className="!bg-white !text-offblack shadow-md hover:!bg-lightgray"
              onClick={animatePins}
            >
              My Post
            </AdminFilterChipButton>
          </div>

          <div className="absolute bottom-14 right-4 bg-white p-1.5 rounded shadow-md space-y-1">
            {[
              { color: 'bg-warmyellow', label: 'Views' },
              { color: 'bg-freshgreen', label: 'Save' },
              { color: 'bg-primaryblue', label: 'Share' },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-1">
                <div className={`w-2.5 h-2.5 rounded shrink-0 ${item.color}`} />
                <AdminMetadataText className="text-offblack capitalize">{item.label}</AdminMetadataText>
              </div>
            ))}
          </div>
        </div>
      </div>
    </DashboardMainContainer>
    </>
  );
};

export default PostCoverageMapWidget;
